import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ColorLens as ColorLensIcon,
  FormatSize as FormatSizeIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import { Banner } from '../StoreBanner';
import ImageUpload from '../../common/ImageUpload';

interface BannerSettingsProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

// خيارات حجم الخط للعنوان الرئيسي
const fontSizeOptions = [
  { value: '1rem', label: 'صغير جداً (1rem)' },
  { value: '1.25rem', label: 'صغير (1.25rem)' },
  { value: '1.5rem', label: 'متوسط (1.5rem)' },
  { value: '1.75rem', label: 'كبير (1.75rem)' },
  { value: '2rem', label: 'كبير جداً (2rem)' },
  { value: '2.5rem', label: 'ضخم (2.5rem)' },
  { value: '3rem', label: 'ضخم جداً (3rem)' },
  { value: '3.5rem', label: 'عملاق (3.5rem)' },
];

// خيارات حجم الخط للعنوان الفرعي
const subtitleFontSizeOptions = [
  { value: '0.75rem', label: 'صغير جداً (0.75rem)' },
  { value: '0.875rem', label: 'صغير (0.875rem)' },
  { value: '1rem', label: 'متوسط (1rem)' },
  { value: '1.25rem', label: 'كبير (1.25rem)' },
  { value: '1.5rem', label: 'كبير جداً (1.5rem)' },
  { value: '1.75rem', label: 'ضخم (1.75rem)' },
  { value: '2rem', label: 'ضخم جداً (2rem)' },
];

// خيارات حجم البانر
const bannerSizeOptions = [
  { value: 'small', label: 'صغير' },
  { value: 'medium', label: 'متوسط' },
  { value: 'large', label: 'كبير' },
];

const BannerSettings: React.FC<BannerSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // تحديث البيانات المحلية عند تغيير الإعدادات
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  // حالة مربع حوار البانر
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [currentBanner, setCurrentBanner] = useState<Banner | null>(null);
  const [isNewBanner, setIsNewBanner] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('display', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات البانر');
    } finally {
      onSave(false);
    }
  };

  // وظائف البانر
  const handleOpenBannerDialog = (banner?: Banner) => {
    console.log('فتح نافذة البانر:', banner);
    
    if (banner) {
      // التأكد من وجود قيم أحجام الخط وحجم البانر
      const bannerWithDefaults = {
        ...banner,
        titleFontSize: banner.titleFontSize || '2rem',
        subtitleFontSize: banner.subtitleFontSize || '1rem',
        size: banner.size || 'medium'
      };
      console.log('تهيئة البانر بالقيم الافتراضية:', bannerWithDefaults);
      setCurrentBanner(bannerWithDefaults);
      setIsNewBanner(false);
    } else {
      setCurrentBanner({
        id: `banner-${Date.now()}`,
        title: '',
        subtitle: '',
        imageUrl: '',
        buttonText: 'تسوق الآن',
        buttonLink: '/store/categories',
        backgroundColor: '#0d51d6',
        textColor: '#ffffff',
        titleFontSize: '2rem',
        subtitleFontSize: '1rem',
        size: 'medium'
      });
      setIsNewBanner(true);
    }
    setBannerDialogOpen(true);
    setUploadError(null);
  };

  const handleCloseBannerDialog = () => {
    setBannerDialogOpen(false);
    setCurrentBanner(null);
    setUploadError(null);
  };

  const handleSaveBanner = async () => {
    if (!currentBanner) return;

    // التحقق من وجود صورة البانر
    if (!currentBanner.imageUrl) {
      setUploadError('الرجاء تحميل صورة للبانر قبل الحفظ');
      return;
    }

    try {
      onSave(true);

      // التأكد من وجود قيم لحجم الخط وحجم البانر وإضافة الوحدات إذا لزم الأمر
      let titleSize = currentBanner.titleFontSize || '2rem';
      let subtitleSize = currentBanner.subtitleFontSize || '1rem';
      
      // التأكد من أن حجم الخط يحتوي على الوحدات (rem أو px أو %)
      if (!titleSize.includes('rem') && !titleSize.includes('px') && !titleSize.includes('%')) {
        titleSize = titleSize + 'rem';
      }
      if (!subtitleSize.includes('rem') && !subtitleSize.includes('px') && !subtitleSize.includes('%')) {
        subtitleSize = subtitleSize + 'rem';
      }
      
      console.log('حفظ أحجام الخط:', titleSize, subtitleSize);
      
      const bannerToSave = {
        ...currentBanner,
        titleFontSize: titleSize,
        subtitleFontSize: subtitleSize,
        size: currentBanner.size || 'medium'
      };

      console.log('حفظ البانر:', bannerToSave);

      let updatedBanners: Banner[];

      if (isNewBanner) {
        updatedBanners = [...formData.banners, bannerToSave];
      } else {
        updatedBanners = formData.banners.map(banner =>
          banner.id === bannerToSave.id ? bannerToSave : banner
        );
      }

      // تحديث البيانات المحلية
      setFormData(prev => ({ ...prev, banners: updatedBanners }));

      // حفظ التغييرات في قاعدة البيانات
      console.log('حفظ البانرات في قاعدة البيانات:', updatedBanners);
      await updateSection('display', { ...formData, banners: updatedBanners });

      // إغلاق مربع الحوار
      handleCloseBannerDialog();

      // عرض رسالة نجاح
      onSuccess(true);
    } catch (error) {
      console.error('خطأ في حفظ البانر:', error);
      onError('حدث خطأ أثناء حفظ البانر');
    } finally {
      onSave(false);
    }
  };

  const handleDeleteBanner = async (bannerId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا البانر؟')) {
      try {
        onSave(true);

        // تحديث البيانات المحلية
        const updatedBanners = formData.banners.filter(banner => banner.id !== bannerId);
        setFormData(prev => ({ ...prev, banners: updatedBanners }));

        // حفظ التغييرات في قاعدة البيانات
        console.log('حفظ البانرات بعد الحذف:', updatedBanners);
        await updateSection('display', { ...formData, banners: updatedBanners });

        // عرض رسالة نجاح
        onSuccess(true);
      } catch (error) {
        console.error('خطأ في حذف البانر:', error);
        onError('حدث خطأ أثناء حذف البانر');
      } finally {
        onSave(false);
      }
    }
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    if (!currentBanner) return;

    const { name, value } = e.target;
    console.log(`تغيير ${name} إلى ${value} للبانر:`, currentBanner.id);
    
    setCurrentBanner(prev => {
      if (!prev) return null;
      const updated = { ...prev, [name as string]: value };
      console.log('البانر بعد التحديث:', updated);
      return updated;
    });
  };

  const handleApplyFontSize = (name: string) => {
    if (!currentBanner) return;
    
    let value = currentBanner[name as keyof Banner] as string || (name === 'titleFontSize' ? '2rem' : '1rem');
    
    // إضافة وحدة القياس إذا لم تكن موجودة
    if (!value.includes('rem') && !value.includes('px') && !value.includes('%')) {
      value = value + 'rem';
    }
    
    console.log(`تطبيق ${name} بقيمة ${value}`);
    
    setCurrentBanner(prev => {
      if (!prev) return null;
      return { ...prev, [name]: value };
    });
  };

  const handleImageUploaded = (imageUrl: string) => {
    if (currentBanner) {
      console.log('تم تحميل صورة البانر بنجاح:', imageUrl);
      setCurrentBanner({
        ...currentBanner,
        imageUrl: imageUrl
      });
      setUploadError(null);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات البانر
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              console.log('تم النقر على زر التعديل');
              setEditing(true);
            }}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      {/* زر تفعيل التحرير */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => {
            console.log('تفعيل وضع التحرير');
            setEditing(true);
          }}
        >
          انقر هنا لتفعيل التحرير
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          البانرات ({formData.banners.length})
        </Typography>

        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => handleOpenBannerDialog()}
          disabled={!editing}
        >
          إضافة بانر جديد
        </Button>
      </Box>

      {formData.banners.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          لا توجد بانرات. قم بإضافة بانر جديد لعرضه في الصفحة الرئيسية للمتجر.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {formData.banners.map((banner) => (
            <Grid item xs={12} md={6} key={banner.id}>
              <Card sx={{ height: '100%' }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={banner.imageUrl || 'https://via.placeholder.com/400x200?text=Banner'}
                  alt={banner.title}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Error+Loading+Image';
                  }}
                />
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {banner.title}
                  </Typography>
                  {banner.subtitle && (
                    <Typography variant="body2" color="text.secondary">
                      {banner.subtitle}
                    </Typography>
                  )}
                  {banner.buttonText && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      زر: {banner.buttonText}
                    </Typography>
                  )}
                  {banner.buttonLink && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      رابط: {banner.buttonLink}
                    </Typography>
                  )}
                  {banner.titleFontSize && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      حجم خط العنوان: {banner.titleFontSize}
                    </Typography>
                  )}
                  {banner.subtitleFontSize && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      حجم خط العنوان الفرعي: {banner.subtitleFontSize}
                    </Typography>
                  )}
                  {banner.size && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      حجم البانر: {banner.size === 'small' ? 'صغير' : banner.size === 'medium' ? 'متوسط' : 'كبير'}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenBannerDialog(banner)}
                    disabled={!editing}
                  >
                    تعديل
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteBanner(banner.id)}
                    disabled={!editing}
                  >
                    حذف
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* مربع حوار إضافة/تعديل البانر */}
      <Dialog
        open={bannerDialogOpen}
        onClose={handleCloseBannerDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNewBanner ? 'إضافة بانر جديد' : 'تعديل البانر'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="العنوان"
                name="title"
                value={currentBanner?.title || ''}
                onChange={handleBannerChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="العنوان الفرعي"
                name="subtitle"
                value={currentBanner?.subtitle || ''}
                onChange={handleBannerChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  صورة البانر (يفضل أن تكون بأبعاد 1200×400 بكسل)
                </Typography>
                <ImageUpload
                  initialImage={currentBanner?.imageUrl || ''}
                  onImageUploaded={handleImageUploaded}
                  label="صورة البانر"
                  folder="banners"
                />
                {uploadError && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {uploadError}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="نص الزر"
                name="buttonText"
                value={currentBanner?.buttonText || ''}
                onChange={handleBannerChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رابط الزر"
                name="buttonLink"
                value={currentBanner?.buttonLink || ''}
                onChange={handleBannerChange}
                helperText="مثال: /store/categories"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="لون الخلفية"
                name="backgroundColor"
                value={currentBanner?.backgroundColor || '#0d51d6'}
                onChange={handleBannerChange}
                type="color"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="لون النص"
                name="textColor"
                value={currentBanner?.textColor || '#ffffff'}
                onChange={handleBannerChange}
                type="color"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* قسم التحكم في حجم الخط */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                تحكم في حجم الخط 🔤
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                أدخل حجم الخط المطلوب مع الوحدة (مثل: 2rem أو 24px أو 150%). يمكنك إدخال أي قيمة تختارها واستخدام زر التطبيق لرؤية التغيير مباشرة.
              </Typography>
            </Grid>
            
            {/* حقول حجم الخط */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="حجم خط العنوان"
                name="titleFontSize"
                value={currentBanner?.titleFontSize || '2rem'}
                onChange={handleBannerChange}
                helperText="أدخل القيمة مثل: 2rem أو 24px أو 150%"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FormatSizeIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="primary"
                      onClick={() => handleApplyFontSize('titleFontSize')}
                      sx={{ minWidth: 'auto', p: '4px 8px' }}
                    >
                      تطبيق
                    </Button>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="حجم خط العنوان الفرعي"
                name="subtitleFontSize"
                value={currentBanner?.subtitleFontSize || '1rem'}
                onChange={handleBannerChange}
                helperText="أدخل القيمة مثل: 1rem أو 16px أو 120%"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FormatSizeIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="primary"
                      onClick={() => handleApplyFontSize('subtitleFontSize')}
                      sx={{ minWidth: 'auto', p: '4px 8px' }}
                    >
                      تطبيق
                    </Button>
                  ),
                }}
              />
            </Grid>
            
            {/* حجم البانر */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="حجم البانر"
                name="size"
                value={currentBanner?.size || 'medium'}
                onChange={handleBannerChange}
                select
                helperText="اختر الحجم الكلي للبانر"
              >
                {bannerSizeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            {/* معاينة الصورة */}
            {currentBanner?.imageUrl && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  معاينة الصورة
                </Typography>
                <Box
                  component="img"
                  src={currentBanner.imageUrl}
                  alt="معاينة البانر"
                  sx={{
                    width: '100%',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: 1,
                    mt: 1
                  }}
                  onError={(e) => {
                    console.error('خطأ في تحميل معاينة الصورة:', currentBanner.imageUrl);
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Error+Loading+Image';
                    setUploadError('تعذر عرض صورة المعاينة. قد تكون رابط الصورة غير صالح.');
                  }}
                />
              </Grid>
            )}
            
            {/* معاينة البانر مع النص وأحجام الخط */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                معاينة البانر
              </Typography>
              <Paper 
                sx={{ 
                  p: 3, 
                  mt: 1, 
                  background: currentBanner?.backgroundColor || '#0d51d6',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1
                }}
              >
                {/* طبقة داكنة شفافة لتحسين قراءة النص */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    zIndex: 0
                  }}
                />
                
                <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'right' }}>
                  <Typography 
                    sx={{ 
                      fontSize: currentBanner?.titleFontSize || '2rem',
                      color: currentBanner?.textColor || '#ffffff',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      mb: 1
                    }}
                  >
                    {currentBanner?.title || 'عنوان البانر الرئيسي'}
                  </Typography>
                  
                  {(currentBanner?.subtitle || !currentBanner) && (
                    <Typography 
                      sx={{ 
                        fontSize: currentBanner?.subtitleFontSize || '1rem',
                        color: currentBanner?.textColor || '#ffffff',
                        mt: 1,
                        textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                        mb: 2
                      }}
                    >
                      {currentBanner?.subtitle || 'العنوان الفرعي للبانر'}
                    </Typography>
                  )}
                  
                  {/* معلومات حجم الخط */}
                  <Box sx={{ mt: 2, backgroundColor: 'rgba(0,0,0,0.5)', p: 1, borderRadius: 1, display: 'inline-block' }}>
                    <Typography variant="caption" sx={{ color: '#fff', display: 'block' }}>
                      ⚙️ حجم خط العنوان: {currentBanner?.titleFontSize || '2rem'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#fff', display: 'block' }}>
                      ⚙️ حجم خط العنوان الفرعي: {currentBanner?.subtitleFontSize || '1rem'}
                    </Typography>
                  </Box>
                  
                  {currentBanner?.buttonText && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        mt: 2,
                        fontWeight: 'bold',
                        backgroundColor: 'white',
                        color: currentBanner.backgroundColor || '#0d51d6',
                        display: 'block',
                        marginRight: 'auto'
                      }}
                    >
                      {currentBanner.buttonText}
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBannerDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveBanner} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditing(false)}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
      
      <Snackbar
        open={uploadError !== null}
        autoHideDuration={6000}
        onClose={() => setUploadError(null)}
        message={uploadError}
      />
    </Box>
  );
};

export default BannerSettings;
